import { useCallback, useEffect, useRef, useState } from 'react';
import { base44 } from '@/api/base44Client';

const POLL_MS = 2500;

/**
 * Drives a remote venue payment request — a scan-to-pay code or a push to the
 * Square Terminal. Raises the request, watches it while it is on screen so a
 * completed payment is noticed with no staff action, and hands the finished sale
 * back through onPaid.
 */
export default function usePosPaymentRequest({ functionName, payload, basketKey, onPaid, onCancel }) {
  // Held in refs so a re-render never restarts a live request.
  const payloadRef = useRef(payload);
  payloadRef.current = payload;
  const onPaidRef = useRef(onPaid);
  onPaidRef.current = onPaid;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  const [request, setRequest] = useState(null);
  const [status, setStatus] = useState('starting');
  const [error, setError] = useState('');
  const [expiresAt, setExpiresAt] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const settledRef = useRef(false);

  const absorb = useCallback((data) => {
    if (!data) return;
    if (data.requestId) setRequest(data);

    if (data.status === 'paid') {
      setStatus('paid');
      if (!settledRef.current) {
        settledRef.current = true;
        onPaidRef.current?.(data.result || {});
      }
      return;
    }
    if (data.status === 'expired') { setStatus('expired'); setError(''); return; }
    if (data.status === 'cancelled') { setStatus('cancelled'); return; }
    if (data.status === 'failed') {
      setStatus('failed');
      setError(data.error || 'Payment received, but the sale could not be completed.');
      return;
    }
    setStatus('pending');
    if (data.expiresAt) setExpiresAt(data.expiresAt);
  }, []);

  const start = useCallback(async (overrides) => {
    setStatus('starting');
    setError('');
    setExpiresAt(null);
    settledRef.current = false;
    try {
      const res = await base44.functions.invoke(functionName, {
        action: 'create',
        basketKey,
        payload: payloadRef.current,
        ...(overrides || {}),
      });
      if (res.data?.success) {
        absorb(res.data);
      } else {
        setStatus('failed');
        setError(res.data?.error || 'The payment request could not be started.');
      }
    } catch (e) {
      setStatus('failed');
      setError(e?.message || 'The payment request could not be started.');
    }
  }, [functionName, basketKey, absorb]);

  useEffect(() => { start(); }, [start]);

  // Watch the live request — this is what notices the payment land by itself.
  useEffect(() => {
    if (status !== 'pending' || !request?.requestId) return undefined;
    const timer = setInterval(async () => {
      try {
        const res = await base44.functions.invoke(functionName, {
          action: 'status',
          requestId: request.requestId,
        });
        if (res.data?.success) absorb(res.data);
        else if (res.data?.error) setError(res.data.error);
      } catch {
        // A dropped poll is not a failure — the next one picks it up.
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [status, request?.requestId, functionName, absorb]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const secondsLeft = expiresAt
    ? Math.max(0, Math.round((new Date(expiresAt).getTime() - now) / 1000))
    : null;

  const withdraw = useCallback(async () => {
    if (!request?.requestId) return;
    try {
      await base44.functions.invoke(functionName, { action: 'cancel', requestId: request.requestId });
    } catch {
      // Withdrawn best-effort — the request still times out on its own.
    }
  }, [functionName, request?.requestId]);

  const cancel = useCallback(async () => {
    await withdraw();
    onCancelRef.current?.();
  }, [withdraw]);

  // Used both for a timed-out code and for moving the sale to another Terminal:
  // the live request is withdrawn first so it can never also be paid.
  const refresh = useCallback(async (overrides) => {
    await withdraw();
    await start(overrides);
  }, [withdraw, start]);

  return { request, status, error, secondsLeft, cancel, refresh };
}